/**
 * PaneControls — the show/hide furniture for the Search page's three panes.
 *
 * The panes used to fold from three different-looking controls: a round chevron hanging off
 * the filter column's edge, a bare chevron at the end of the map's header, and another in the
 * results' status bar. None of them said WHICH pane it folded, and two of them looked like
 * every other small button around them. So every one of them is now the same object:
 *
 *   - `PaneGlyph` — a window split in three with ONE part filled. The filled part is the pane
 *     the control is about (left = filters, middle = map, right = results), so the icon names
 *     its target without words.
 *   - `HidePaneButton` — that glyph plus the word "Hide", in each pane's own header.
 *   - `PaneRail` — a folded pane: the whole strip is the button back, with a chevron saying
 *     which way it opens.
 *
 * The page header carries the same three glyphs as a toggle group, so the layout can be read
 * and changed from one place too.
 */
import type { ComponentType, SVGProps } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export type PaneSide = 'left' | 'center' | 'right';

/** A three-pane window with the named pane filled in. */
export function PaneGlyph({ side, className = 'h-4 w-4' }: { side: PaneSide; className?: string }) {
  const fill = side === 'left' ? { x: 1.65, w: 3.85 } : side === 'center' ? { x: 5.5, w: 5 } : { x: 10.5, w: 3.85 };
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden fill="none">
      <rect x={fill.x} y={3.15} width={fill.w} height={9.7} fill="currentColor" opacity={0.85} />
      <rect x={1} y={2.5} width={14} height={11} rx={2} stroke="currentColor" strokeWidth={1.3} />
      <line x1={5.5} y1={2.5} x2={5.5} y2={13.5} stroke="currentColor" strokeWidth={1.1} />
      <line x1={10.5} y1={2.5} x2={10.5} y2={13.5} stroke="currentColor" strokeWidth={1.1} />
    </svg>
  );
}

/** "Hide", in a pane's own header — the glyph says which pane. */
export function HidePaneButton({
  side, label, onClick,
}: { side: PaneSide; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Hide ${label}`}
      aria-label={`Hide ${label}`}
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-border bg-card px-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-brand hover:text-brand"
    >
      <PaneGlyph side={side} className="h-3.5 w-3.5" />
      Hide
    </button>
  );
}

/**
 * A folded pane. The WHOLE strip is the button back — a 44px target the full height of the
 * page rather than a 20px icon somewhere on it — and it highlights as one on hover.
 */
export function PaneRail({
  label, Icon, count, onExpand, opens, className = '',
}: {
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** A figure worth seeing while folded — results found, filters active. */
  count?: number;
  onExpand: () => void;
  /** Which way the pane grows back out, so the chevron points the right way. */
  opens: 'left' | 'right';
  /** Borders and positioning, which depend on where the rail sits. */
  className?: string;
}) {
  const Chevron = opens === 'right' ? ChevronRightIcon : ChevronLeftIcon;
  return (
    <button
      type="button"
      onClick={onExpand}
      title={`Show ${label.toLowerCase()}`}
      aria-label={`Show ${label.toLowerCase()}`}
      className={`group flex w-11 shrink-0 flex-col items-center gap-2.5 bg-card pt-2 transition-colors hover:bg-muted ${className}`}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-brand shadow-sm transition-colors group-hover:border-brand">
        <Chevron className="h-4 w-4 stroke-2" />
      </span>
      <span className="relative text-muted-foreground transition-colors group-hover:text-foreground">
        <Icon className="h-5 w-5" />
        {typeof count === 'number' && count > 0 && (
          <span className="absolute -right-2 -top-1.5 rounded-full bg-brand px-1 text-[9px] font-semibold leading-[0.95rem] text-white">
            {count > 999 ? '999+' : count}
          </span>
        )}
      </span>
      <span
        className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors group-hover:text-foreground"
        style={{ writingMode: 'vertical-rl' }}
      >
        {label}
      </span>
    </button>
  );
}

/** The page header's layout switch: one button per pane, lit while that pane is showing. */
export function PaneToggles({
  panes,
}: {
  panes: Array<{ side: PaneSide; label: string; on: boolean; onToggle: () => void }>;
}) {
  return (
    <div
      role="group"
      aria-label="Show or hide panels"
      className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5"
    >
      {panes.map((p) => (
        <button
          key={p.side}
          type="button"
          aria-pressed={p.on}
          onClick={p.onToggle}
          title={`${p.on ? 'Hide' : 'Show'} ${p.label.toLowerCase()}`}
          className={`flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition-colors ${
            p.on
              ? 'bg-card text-brand shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:bg-card hover:text-foreground'
          }`}
        >
          <PaneGlyph side={p.side} />
          <span className="hidden xl:inline">{p.label}</span>
        </button>
      ))}
    </div>
  );
}
